/**
 * extractLabelLines
 *
 * Pure: turns a JointJS cell + the user's toggle state into the list of text
 * lines that should be rendered under the object on the canvas. Empty values
 * are skipped so a label with only IP set just shows the IP, not blank rows.
 *
 * Lines order is fixed (ID -> IP -> NAME), matching the toggle UI left-to-right.
 *
 * Used by useObjectInfoLabels. No React / no DOM here — kept pure so tests can
 * exercise it without mocking JointJS internals.
 */

import { dia } from '@joint/core'
import { ObjectLabelToggles } from './objectLabelToggleStorage'

/**
 * Length of the truncated id displayed under the object. Bumped from the
 * project-wide first-8 convention to 30 chars for this overlay because
 * users on dense parking maps need to disambiguate ids like
 * `p-parkingLocations-007` that share the same first 8 chars. Full UUIDs
 * are still truncated to keep label width bounded; 30 chars covers every
 * typical layer-name + suffix pattern in the parking template.
 */
export const OBJECT_LABEL_ID_LENGTH = 30

interface CellDataShape {
  properties?: {
    name?: unknown
    ipAddress?: unknown
    [key: string]: unknown
  }
  groupName?: unknown
}

function asTrimmedString(value: unknown): string {
  if (typeof value !== 'string') return ''
  return value.trim()
}

/**
 * Returns the ordered list of text lines for a given cell, given which fields
 * the user toggled on. Returns [] when nothing should be drawn (so the caller
 * can skip the cell entirely instead of producing an empty SVG <g>).
 */
export function extractLabelLines(
  cell: dia.Cell,
  toggles: ObjectLabelToggles,
): string[] {
  const lines: string[] = []
  const data = (cell.get('data') as CellDataShape | undefined) ?? {}
  const properties = data.properties ?? {}

  if (toggles.id) {
    const fullId = String(cell.id ?? '')
    if (fullId.length > 0) {
      lines.push(
        fullId.length > OBJECT_LABEL_ID_LENGTH
          ? fullId.slice(0, OBJECT_LABEL_ID_LENGTH)
          : fullId,
      )
    }
  }

  if (toggles.ip) {
    const ip = asTrimmedString(properties.ipAddress)
    if (ip) lines.push(ip)
  }

  if (toggles.name) {
    const name =
      asTrimmedString(properties.name) || asTrimmedString(data.groupName)
    if (name) lines.push(name)
  }

  return lines
}
